import React from "react";
import SEO from "../components/SEO";
import TransitionLink from "../components/TransitionLink";
import LightHeader from "../components/LightHeader";
import LightFooter from "../components/LightFooter";
import { useLanguage } from "../context/LanguageContext";

const content = {
  en: {
    tag: "Teaching / Virtual Production and 3D",
    h1a: "Virtual Production",
    h1b: "and 3D Instructor",
    getInTouch: "Get in touch",
    bio: "Fifteen years of hands-on broadcast production, two of them also in the classroom. I taught virtual production and advanced 3D at ITS Apulia Digital Maker. I'm an Unreal Authorized Instructor, qualified directly by Epic Games.",
    uaiAlt: "Epic Games Authorized Unreal Engine Instructor",
    blenderAlt: "Blender Expert",
    teachingExperience: "Teaching Experience",
    itsPeriod: "2020 - 2023",
    itsTitle: "ITS Apulia Digital Maker",
    itsBody:
      "Taught virtual production and advanced 3D to students entering the broadcast and media industry. Curriculum covering Unreal Engine, real-time pipelines, camera tracking and production workflow.",
    itsFootnote: "Two students from the course later worked with me. One still collaborates today.",
    subjectsLabel: "Subjects",
    whatITeach: "What I Teach",
    subjects: [
      "Virtual production and real-time rendering",
      "3D modeling, rigging and animation",
      "Broadcast graphics and motion design",
      "Augmented reality for live television",
      "Python scripting and tool development",
      "Web development for creative professionals",
    ],
    pillars: [
      {
        title: "Practice before theory",
        body: "Every concept I teach comes from something I built and delivered under real broadcast conditions. Students learn tools that are actually used on set.",
      },
      {
        title: "Industry contacts",
        body: "Fifteen years in Italian national television left me with a network in the broadcast industry, useful when a student is looking for their first opportunities.",
      },
      {
        title: "Cross-disciplinary skills",
        body: "3D, real-time engines, AR, web development, scripting. A path that combines creative and technical skills, useful for anyone who wants to move between the two.",
      },
    ],
    productionBackground: "Production Background",
    viewProject: "View project",
    ctaText: "For course information or teaching collaborations, get in touch.",
    fullPortfolio: "Full portfolio",
    projects: [
      {
        id: "1",
        title: "Viva Rai2!",
        category: "Broadcast Live / Motion Graphics",
        year: "2023-2024",
        description:
          "Daily real-time graphics for Fiorello's national morning show on Rai 2, with very tight delivery deadlines.",
        image: "/img/work1.png",
        link: "/work/1",
      },
      {
        id: "3",
        title: "Ulisse — Alberto Angela",
        category: "Virtual Production",
        year: "2020-2022",
        description:
          "End-to-end virtual production for one of the most watched formats on Italian television. Camera tracking, real-time compositing, full pipeline management.",
        image: "/img/work3.png",
        link: "/work/3",
      },
      {
        id: "5",
        title: "Virtual Studio and AR — LA7 / Rainews24",
        category: "Augmented Reality / Virtual Studio",
        year: "2014-2023",
        description:
          "Nine years of AR and virtual studio work for national news and Italian political broadcast coverage. Systems still on air today.",
        image: "/img/work5.png",
        link: "/work/5",
      },
    ],
  },
  it: {
    tag: "Docenza / Virtual Production e 3D",
    h1a: "Docente",
    h1b: "Virtual Production e 3D",
    getInTouch: "Contattami",
    bio: "Quindici anni di produzione broadcast sul campo, due dei quali anche in aula. Ho insegnato virtual production e 3D avanzato all'ITS Apulia Digital Maker. Sono Unreal Authorized Instructor, qualificato direttamente da Epic Games.",
    uaiAlt: "Epic Games Authorized Unreal Engine Instructor",
    blenderAlt: "Blender Expert",
    teachingExperience: "Esperienza Didattica",
    itsPeriod: "2020 - 2023",
    itsTitle: "ITS Apulia Digital Maker",
    itsBody:
      "Insegnato virtual production e 3D avanzato a studenti che entravano nel settore broadcast e media. Curriculum su Unreal Engine, pipeline real-time, camera tracking e workflow produttivo.",
    itsFootnote: "Due studenti del corso hanno poi lavorato con me. Uno collabora ancora oggi.",
    subjectsLabel: "Materie",
    whatITeach: "Cosa Insegno",
    subjects: [
      "Virtual production e rendering real-time",
      "Modellazione 3D, rigging e animazione",
      "Grafica broadcast e motion design",
      "Realta aumentata per la televisione live",
      "Scripting Python e sviluppo tool",
      "Sviluppo web per professionisti creativi",
    ],
    pillars: [
      {
        title: "Pratica prima della teoria",
        body: "Ogni concetto che insegno viene da qualcosa che ho costruito e consegnato in condizioni broadcast reali. Gli studenti imparano strumenti che vengono davvero usati sul set.",
      },
      {
        title: "Contatti nel settore",
        body: "Quindici anni nella televisione nazionale italiana mi hanno lasciato una rete di contatti nel settore broadcast, utile quando uno studente cerca le prime occasioni di lavoro.",
      },
      {
        title: "Competenze trasversali",
        body: "3D, motori real-time, AR, sviluppo web, scripting. Un percorso che unisce competenze creative e tecniche, utile per chi vuole muoversi tra i due ambiti.",
      },
    ],
    productionBackground: "Background Produttivo",
    viewProject: "Vedi progetto",
    ctaText: "Per informazioni sui corsi o per una collaborazione didattica, scrivimi.",
    fullPortfolio: "Portfolio completo",
    projects: [
      {
        id: "1",
        title: "Viva Rai2!",
        category: "Broadcast Live / Motion Graphics",
        year: "2023-2024",
        description:
          "Grafica in tempo reale ogni giorno per il morning show nazionale di Fiorello su Rai 2, con tempi di consegna molto stretti.",
        image: "/img/work1.png",
        link: "/work/1",
      },
      {
        id: "3",
        title: "Ulisse — Alberto Angela",
        category: "Virtual Production",
        year: "2020-2022",
        description:
          "Virtual production end-to-end per uno dei formati piu visti della televisione italiana. Camera tracking, compositing real-time, gestione completa della pipeline.",
        image: "/img/work3.png",
        link: "/work/3",
      },
      {
        id: "5",
        title: "Studio Virtuale e AR — LA7 / Rainews24",
        category: "Realta Aumentata / Studio Virtuale",
        year: "2014-2023",
        description:
          "Nove anni di lavoro in AR e studio virtuale per il telegiornale nazionale e il broadcast politico italiano. Sistemi ancora in onda oggi.",
        image: "/img/work5.png",
        link: "/work/5",
      },
    ],
  },
};

const LandingTrainer = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <>
      <SEO
        title={language === "it" ? "Docente Virtual Production e 3D" : "Virtual Production and 3D Instructor"}
        description={
          language === "it"
            ? "Quindici anni di produzione broadcast sul campo, due anni di insegnamento all'ITS Apulia Digital Maker. Virtual production, 3D avanzato, pipeline real-time."
            : "Fifteen years of hands-on broadcast production, two years teaching at ITS Apulia Digital Maker. Virtual production, advanced 3D, real-time pipelines."
        }
        url="/landing-trainer"
        language={language}
      />

      <div
        className="min-h-screen bg-black"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        <LightHeader ctaLabel={t.getInTouch} />

        {/* Hero */}
        <section className="pt-40 pb-24 px-8 md:px-16 max-w-6xl mx-auto">
          <p className="text-orange-400 text-sm font-mono tracking-widest uppercase mb-6">
            {t.tag}
          </p>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black text-white tracking-tight leading-none mb-10">
            {t.h1a}
            <br />
            <span className="text-orange-400">{t.h1b}</span>
          </h1>
          <div className="flex items-center gap-8 mb-10">
            <img
              src="/img/UAI_trainer.png"
              alt={t.uaiAlt}
              className="w-36 md:w-44 h-auto"
            />
            <div className="flex items-center gap-3">
              <img
                src="/img/blender_logo.png"
                alt={t.blenderAlt}
                className="w-36 md:w-44 h-auto"
              />
            </div>
          </div>
          <p className="text-xl md:text-2xl text-white/70 font-light leading-relaxed max-w-2xl mb-12">
            {t.bio}
          </p>
          <a
            href="mailto:info@paoloallegretti.com"
            className="inline-flex items-center text-white font-medium text-lg group"
          >
            <span className="border-b border-white/30 group-hover:border-orange-400 group-hover:text-orange-400 transition-colors duration-300 pb-0.5">
              info@paoloallegretti.com
            </span>
            <span className="ml-2 group-hover:translate-x-1 transition-transform duration-300">
              →
            </span>
          </a>
        </section>

        {/* Teaching experience */}
        <section className="border-t border-b border-white/10 py-16 px-8 md:px-16">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-12">
              {t.teachingExperience}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="border border-white/10 rounded-2xl p-8 hover:border-orange-400/30 transition-colors duration-300">
                <p className="text-xs font-mono text-orange-400 tracking-widest uppercase mb-4">
                  {t.itsPeriod}
                </p>
                <h3 className="text-xl font-black text-white mb-4 tracking-tight">
                  {t.itsTitle}
                </h3>
                <p className="text-white/50 leading-relaxed text-sm mb-6">
                  {t.itsBody}
                </p>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-sm text-white/30 font-mono">
                    {t.itsFootnote}
                  </p>
                </div>
              </div>
              <div className="border border-white/10 rounded-2xl p-8 hover:border-orange-400/30 transition-colors duration-300">
                <p className="text-xs font-mono text-orange-400 tracking-widest uppercase mb-4">
                  {t.subjectsLabel}
                </p>
                <h3 className="text-xl font-black text-white mb-4 tracking-tight">
                  {t.whatITeach}
                </h3>
                <ul className="space-y-3">
                  {t.subjects.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 text-sm text-white/50"
                    >
                      <span className="text-orange-400 mt-0.5 flex-shrink-0">
                        +
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Why it matters */}
        <section className="py-24 px-8 md:px-16">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
            {t.pillars.map((item) => (
              <div
                key={item.title}
                className="border border-white/10 rounded-2xl p-8 hover:border-orange-400/30 transition-colors duration-300"
              >
                <h3 className="text-lg font-black text-white mb-4 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-white/50 leading-relaxed text-sm">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Production proof */}
        <section className="py-16 px-8 md:px-16 border-t border-white/10 max-w-6xl mx-auto">
          <h2 className="text-sm font-mono text-white/40 tracking-widest uppercase mb-16">
            {t.productionBackground}
          </h2>
          <div className="space-y-6">
            {t.projects.map((project) => (
              <TransitionLink
                key={project.id}
                to={project.link}
                className="group block border border-white/10 rounded-2xl overflow-hidden hover:border-orange-400/40 transition-colors duration-300"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="w-full md:w-64 h-48 md:h-auto bg-white/5 flex-shrink-0 overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  </div>
                  <div className="p-8 flex flex-col justify-between flex-1">
                    <div>
                      <div className="flex items-center gap-4 mb-4">
                        <span className="text-xs font-mono text-orange-400 tracking-widest uppercase">
                          {project.category}
                        </span>
                        <span className="text-xs font-mono text-white/30">
                          {project.year}
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tight group-hover:text-orange-400 transition-colors duration-300">
                        {project.title}
                      </h3>
                      <p className="text-white/50 leading-relaxed">
                        {project.description}
                      </p>
                    </div>
                    <div className="mt-6">
                      <span className="text-sm text-white/30 font-mono group-hover:text-orange-400 transition-colors duration-300">
                        {t.viewProject} →
                      </span>
                    </div>
                  </div>
                </div>
              </TransitionLink>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="py-24 px-8 md:px-16">
          <div className="max-w-6xl mx-auto">
            <p className="text-3xl md:text-5xl font-black text-white tracking-tight leading-tight mb-10 max-w-2xl">
              {t.ctaText}
            </p>
            <a
              href="mailto:info@paoloallegretti.com"
              className="text-3xl md:text-5xl font-black text-white/30 hover:text-orange-400 transition-colors duration-300 tracking-tight"
            >
              info@paoloallegretti.com
            </a>
            <LightFooter fullPortfolioLabel={t.fullPortfolio} />
          </div>
        </section>
      </div>
    </>
  );
};

export default LandingTrainer;
